import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { request, businessCode } from './index'

export type CustomerBookingField = {
  name: string
  display: boolean
  required: boolean
  translationKey: string
  type: string
  custom_field_id: number | null
  values?: Array<{id: number, value: string, active: boolean, position: number}>
}

export type CustomerType = {
  id?: number
  firstname?: string
  lastname?: string
  avatar?: string
  email?: string
  phone?: string
  resource?: number
  self_order?: boolean
  allow_book: boolean
  customers: Array<CustomerType>
  unfilled_fields?: CustomerBookingField[]
  loaded: boolean
  loggedIn: boolean
  confirmed: boolean
}

type StateType = {
  data: CustomerType
  unfilled_fields: CustomerBookingField[]
  temp: { phone: string, name: string}
  selectedCustomerId: number | null
  bookingFieldValues: Record<string, any>
}

const initialState: StateType = {
  data: {
    loaded: false,
    loggedIn: false,
    confirmed: false,
    allow_book: true,
    customers: [],
  },
  unfilled_fields: [],
  temp: {
    phone: '',
    name: '',
  },
  selectedCustomerId: null,
  bookingFieldValues: {},
}

export const customerSlice = createSlice({
  name: '$customer',

  initialState,

  reducers: {
    data: (state: StateType, action: PayloadAction<{allow_book: boolean, customer: CustomerType, status: string, customers: Array<CustomerType>}>) => {
      state.data = {
        ...action.payload.customer,
        allow_book: action.payload.allow_book,
        customers: action.payload.customers,
        loaded: true,
        loggedIn: state.data.loggedIn,
      }
    },

    unfilled_fields: (state: StateType, action: PayloadAction<CustomerBookingField[]>) => {
      state.unfilled_fields = action.payload
    },

    loaded: (state: StateType, action: PayloadAction<boolean>) => {
      state.data = {
        ...state.data,
        loaded: action.payload,
      }
    },

    loggedIn: (state: StateType, action: PayloadAction<boolean>) => {
      state.data.loggedIn = action.payload
      if (!action.payload) {
        localStorage.removeItem('customerPhone')
      }
    },

    confirmed: (state: StateType, action: PayloadAction<boolean>) => {
      state.data.confirmed = action.payload
    },

    phone: (state: StateType, action: PayloadAction<string>) => {
      state.temp.phone = action.payload
    },

    name: (state: StateType, action: PayloadAction<string>) => {
      state.temp.name = action.payload
    },

    temp: (state: StateType, action: PayloadAction<{phone: string, name: string}>) => {
      state.temp = action.payload
    },

    selectedCustomerId: (state: StateType, action: PayloadAction<number | null>) => {
      state.selectedCustomerId = action.payload
    },

    bookingFieldValues: (state: StateType, action: PayloadAction<Record<string, any>>) => {
      state.bookingFieldValues = action.payload
    },

    logout: (state: StateType) => {
      state.data = initialState.data
      state.temp = initialState.temp
      state.selectedCustomerId = initialState.selectedCustomerId
      state.bookingFieldValues = initialState.bookingFieldValues

      localStorage.removeItem('customerPhone')
      localStorage.removeItem('customerId')
      localStorage.removeItem('customerTemp')
      localStorage.removeItem('sentAt')
    },
  },
})

const get = {
  phone: createSelector([
    state => state.$customer.data,
    state => state.$customer.temp,
  ], (data, temp) => {
    return data?.phone ?? temp.phone
  }),

  name: createSelector([
    state => state.$customer.data,
    state => state.$customer.temp,
  ], (data, temp) => {
    return data?.firstname ?? temp.name
  }),

  customerId: createSelector([
    state => state.$customer.data,
    state => state.$customer.selectedCustomerId,
  ], (data, selectedCustomerId) => {
    return data?.id ?? selectedCustomerId
  }),

  unfilledFields: createSelector([
    state => state.$customer.data,
    state => state.$customer.unfilled_fields,
  ], (data, unfilled_fields) => {
    if (data?.id) {
      return data.unfilled_fields
    }

    return unfilled_fields
  }),
}

const api = {
  get: (params: object) => {
    return request(`phone/id/${businessCode()}`, 'POST', params)
  },

  getUnfilledFields: () => {
    return request(`unfilled_fields/id/${businessCode()}`)
  },

  sendCode: (params: object) => {
    return request(`send_code/id/${businessCode()}`, 'POST', params)
  },

  checkCode: (params: object) => {
    return request(`check_code/id/${businessCode()}`, 'POST', params)
  },
}

const customer = {
  reducer: customerSlice.reducer,
  set: customerSlice.actions,
  get,
  api,
}

export default customer